import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { FilterBarComponent } from '@modules/filters/filter-bar/filter-bar.component';
import { ScrollTrackerDirective } from '@modules/shared/directives/scroll-tracker.directive';
import { ShowIfClientIsGamEnabledDirective } from '@modules/shared/directives/show-if-client-is-gam-enabled.directive';
import { LoaderComponent } from '@modules/shared/loader/loader.component';
import { SpinnerComponent } from '@modules/shared/spinner/spinner.component';
import { StatusComponent } from '@modules/shared/status/status.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Patient } from '@pixacare/domain';
import { PaginationQuery } from '@pixacare/shared';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { PagedCollection } from '@shared/models/pagination/paged-collection';
import { TuiButton, TuiDialogContext, TuiRadio } from '@taiga-ui/core';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { Status } from 'src/app/shared/models/enums/status.enum';
import { patientPaginationContext } from 'src/app/shared/models/pagination/pagination-context.config';
import { FilterBarService } from '../../filters/filter-bar.service';
import { PatientCardComponent } from '../patient-card/patient-card.component';
import { PatientLinkQuickActionComponent } from '../patient-link-quick-action/patient-link-quick-action.component';
import { PatientPaginationService } from '../patient-pagination.service';

@UntilDestroy()
@Component({
  templateUrl: './patient-merge.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PatientPaginationService, FilterBarService],
  standalone: true,
  imports: [
    TranslocoDirective,
    FilterBarComponent,
    AsyncPipe,
    ScrollTrackerDirective,
    StatusComponent,
    PatientCardComponent,
    PatientLinkQuickActionComponent,
    ShowIfClientIsGamEnabledDirective,
    SpinnerComponent,
    EmptyStateComponent,
    NgTemplateOutlet,
    TuiButton,
    TuiRadio,
    LoaderComponent,
  ],
  host: {
    class: 'flex flex-col gap-3',
    'data-testid': 'patient-merge',
  },
})
export class PatientMergeComponent implements OnInit {
  private readonly context = inject<
    TuiDialogContext<
      Patient,
      {
        patient: Patient;
        clientCode: string;
        departmentId: number;
      }
    >
  >(POLYMORPHEUS_CONTEXT);
  private readonly patientPaginationService = inject(PatientPaginationService);
  private readonly filterBarService = inject(FilterBarService);

  patient: Patient = this.context.data.patient;
  clientCode: string = this.context.data.clientCode;
  departmentId: number = this.context.data.departmentId;

  collection$: Observable<PagedCollection<Patient[]>>;
  isCollectionLoaded$: Observable<boolean> = null;

  targetPatient: Patient = null;
  Status = Status;

  ngOnInit(): void {
    this.filterBarService.init();

    this.isCollectionLoaded$ = this.patientPaginationService.isCollectionLoaded$;
    this.collection$ = this.patientPaginationService.select().pipe(
      untilDestroyed(this),
      map((collection) => ({
        ...collection,
        sorted: collection.sorted.filter((patientId) => patientId !== this.patient.id),
        data: collection.data.filter((patient) => patient.id !== this.patient.id),
      })),
    );

    this.filterBarService.filteredSearch$
      .pipe(untilDestroyed(this), debounceTime(300), distinctUntilChanged())
      .subscribe(({ search }) => {
        this.patientPaginationService.load(this.clientCode, {
          reset: true,
          query: new PaginationQuery({
            orderBy: ['last_activity|desc'],
            size: patientPaginationContext.countPerPage,
            filter: this.departmentId ? [{ prop: 'department_id', op: 'eq', val: this.departmentId.toString() }] : [],
            ...(search && { search }),
          }),
        });

        this.targetPatient = null;
      });
  }

  loadNextPatients(): void {
    this.patientPaginationService.loadNextPage(this.clientCode);
  }

  selectTargetPatient(patient: Patient): void {
    this.targetPatient = patient;
  }

  mergePatients(): void {
    this.context.completeWith(this.targetPatient);
  }

  cancel(): void {
    this.context.completeWith(null);
  }
}
